import { FormGroup, AbstractControlOptions, Validators } from '@angular/forms';
import { FormButtonField } from '../components/form-button/form-button.model';
import { MatDialogRef } from '@angular/material/dialog';
import { FormConfigInstance } from '../services/form.service';

// tslint:disable: no-inferrable-types
// tslint:disable: whitespace

export interface FormValidator {
  name: string;
  validator: any;
  message: string;
}

export type FormTemplate = 'inline' | 'accordion' | 'grid';
export type FormColor = 'primary' | 'accent' | 'warn';
export type FormActionButtons = 'fab' | 'raised' | 'flat' | 'text' | false;

export interface FormBaseConfig<T = any> {
  title?: string;
  name?: string;
  subtitle?: string;
  template?: FormTemplate;
  fields?: FieldConfig[];
  color?: FormColor;
  classes?: string[];
  formValidators?: AbstractControlOptions | { [key: string]: any; } | null;
  actionButtons?: FormActionButtons;
  buttons?: FormButtonField[];
  editable?: boolean;
  initialState?: 'idle' | 'edit';
  titleStyle?: any;
  subTitleStyle?: any;
  formStyle?: any;
  hostStyle?: any;
  actionButtonsStyle?: any;
  showFormErrorToast?: boolean;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  editButtonLabel?: string;
  onShowModal?: (ref: MatDialogRef<any>) => void;
  beforeSave?: (value: T, form: FormGroup) => Promise<boolean>;
  /// Return true if detect changes must be called
  valueChanges?: (value: T, fields: FieldConfig[], service?: FormConfigInstance) => boolean;
  afterInit?: (service: FormConfigInstance) => void;
  afterAddFile?: (file: { file: File, data: string }, field: FieldConfig) => boolean;
  getFieldByName?: <N = any>(name: N) => FieldConfig<N>;
  getFieldByID?: <N = any>(id: N) => FieldConfig;
}


export type FieldType = 'input' | 'checkbox' | 'group' | 'radio' | 'button'
  | 'date' | 'select' | 'label' | 'daterange' | 'fileUpload' | 'container'
  | 'slideToggle' | 'slider' | 'inject' | 'entity' | 'listPicker'
  | 'chips' | 'color';

const FORM_FIELD_DEFAULTS: Partial<FieldConfig<any>> = {
  visible: true,
  color: 'primary'
};

type FieldVisibility<T = any> = boolean | ((values: T, value, service: FormConfigInstance) => boolean);
export interface FieldConfigInterface<N = any> {
  type?: FieldType;
  label?: string;
  name?: N;
  id?: any;
  validations?: FormValidator[];
  readonly required?: boolean;
  readonly?: boolean;
  classes?: string[];
  hostStyle?: any;
  visible?: FieldVisibility;
  color?: 'primary' | 'accent' | 'warn';
  _isVisible?: boolean;
  /**
   * Called on every value change of the control.
   * The return value inform whether the form must call change detection.
   */
  valueChanges?: (field: FieldConfig, fieldValue: any, service: FormConfigInstance) => boolean;

}

export class FieldConfig<N = any> implements FieldConfigInterface<N> {
  type?: FieldType;
  label?: string;
  name?: N;
  id?: any;
  validations?: FormValidator[];
  readonly required?: boolean;
  readonly?: boolean;
  classes?: string[];
  hostStyle?: any;
  containerStyle?: any;
  visible?: FieldVisibility = null;
  color?: 'primary' | 'accent' | 'warn';
  defaultValue?: any;

  _isVisible?= true;

  valueChanges?: (field: FieldConfig, fieldValue: any, service: FormConfigInstance) => boolean;

  constructor(o: FieldConfig) {
    Object.assign(this, FORM_FIELD_DEFAULTS, o);
    if (!this.validations) {
      this.validations = [];
    }

    if (this.required === true) {
      if (!this.validations.find(v => v.name === 'required')) {
        this.validations.push({
          name: 'required',
          validator: Validators.required,
          message: 'Campo obrigatório'
        });
      }
    } else {
      this.required = !!this.validations.find(v => v.name === 'required');
    }

  }
}

export interface FormAccordionPanel {
  title: string;
  fields: FieldConfig[];
  classes?: string[];
  expanded?: boolean;
}
export interface FormAccordionOptions {
  panels: FormAccordionPanel[];
  buttons?: FieldConfig[];
  multi?: boolean;
}

export interface FormFieldComponent<S = any, F = FieldConfig> {
  state: 'idle' | 'edit';
  field: F;
  group: FormGroup;
  service: S;
}

abstract class FormBase<T = any> implements FormBaseConfig<T> {
  title?: string;
  subtitle?: string;
  readonly template?: FormTemplate;
  fields?: FieldConfig[];
  color?: FormColor = 'primary';
  classes?: string[];
  formValidators?: AbstractControlOptions | { [key: string]: any; } | null;
  name?: string;
  titleStyle?: any;
  subTitleStyle?: any;
  formStyle?: any;
  hostStyle?: any;
  editable?: boolean = false;
  initialState?: 'idle' | 'edit' = 'edit';
  actionButtons?: FormActionButtons;
  buttons?: FormButtonField[];
  actionButtonsStyle?: any;
  showFormErrorToast?= true;
  submitButtonLabel?: string = 'SALVAR';
  cancelButtonLabel?: string = 'CANCELAR';
  editButtonLabel?: string = 'EDITAR';

  onShowModal?: (ref: MatDialogRef<any>) => void;
  beforeSave?: (value: T, form: FormGroup) => Promise<boolean>;

  valueChanges?: (value: T, fields: FieldConfig[], service: FormConfigInstance) => boolean;
  afterInit?: (service: FormConfigInstance) => void;
  afterAddFile?: (file: { file: File, data: string }, field: FieldConfig) => boolean;


  constructor(o: FormBaseConfig<T>) {
    Object.assign(this, o);
    if (!this.editable) {
      this.initialState = 'idle';
    }

    if (!o.actionButtons && o.actionButtons !== false) {
      this.actionButtons = 'raised';
    }

  }

  getFieldByName?<N = any>(name: N): FieldConfig<N> {
    let field: FieldConfig<N>;

    const findInFields = (_fields: FieldConfig<N>[]) => {
      if (field) {
        return;
      }
      _fields.forEach(_field => {
        if (field) {
          return;
        }
        if (_field.name === name) {
          field = _field;
          return;
        }

        // daterange
        if ((_field as any).field2) {
          if ((_field as any).field2.name === name) {
            field = (_field as any).field2;
          }
          return;
        }

        if ((_field as any).group) {
          findInFields((_field as any).group);
        }


        if ((_field as any).fields) {
          findInFields((_field as any).fields);
        }
      });
    };
    findInFields(this.fields);

    return field;
  }

  getFieldByID?<N = any>(id: any): FieldConfig<N> {
    let field: FieldConfig;

    const findInFields = (_fields: FieldConfig[]) => {
      if (field) {
        return;
      }
      _fields.forEach(_field => {
        if (field) {
          return;
        }
        if (_field.id === id) {
          field = _field;
          return;
        }

        // daterange
        if ((_field as any).field2) {
          if ((_field as any).field2.id === id) {
            field = (_field as any).field2;
          }
          return;
        }

        if ((_field as any).group) {
          findInFields((_field as any).group);
        }


        if ((_field as any).fields) {
          findInFields((_field as any).fields);
        }
      });
    };

    findInFields(this.fields);
    if (!field && this.buttons && this.buttons.length > 0) {
      findInFields(this.buttons);
    }
    return field;
  }
}
export class FormGrid<T = any> extends FormBase<T> {
  readonly template?: FormTemplate = 'grid';
  constructor(o: FormGrid<T>) {
    super(o);

    if (this.actionButtons != 'fab') {
      this.submitButtonLabel = this.submitButtonLabel || 'OK';
      this.cancelButtonLabel = this.cancelButtonLabel || 'CANCELAR';
    }
  }
}

export interface IFormInlineOptions {
  hostStyle: any;
}

export class FormInline<T = any> extends FormBase<T> {
  readonly template?: FormTemplate = 'inline';

  options?: IFormInlineOptions;


  constructor(o: FormInline<T>) {
    super(o);
  }
}
export class FormAccordion<T = any> extends FormBase<T> {
  readonly template?: FormTemplate = 'accordion';
  accordion: FormAccordionOptions;
  constructor(o: FormAccordion) {
    super(o);
  }
}

